/**
 * QUIPWA — schema migrations
 *
 * Every database has a baseline DDL (idempotent `CREATE … IF NOT EXISTS`) and
 * an ordered list of numbered migrations. `PRAGMA user_version` records the
 * last migration applied.
 *
 * Why this replaced the old approach: the old schema string ended with
 * `PRAGMA user_version = 5`, and it ran on *every* start before the version
 * check — so every database always reported version 5 and the v2–v4 steps
 * never ran. New databases kept a stale `deck_stats` view that double-counted
 * overdue cards. Here the baseline never touches `user_version`.
 *
 * ```ts
 * await applySchema(adapter, {
 *   baseline: STATE_DB_SCHEMA,
 *   baselineVersion: STATE_BASELINE_VERSION,
 *   migrations: STATE_MIGRATIONS,
 * })
 * ```
 */

import type { IDBAdapter } from './db-adapter.ts'

export interface Migration {
  /** Target `user_version`; must be strictly increasing and > baselineVersion. */
  version: number
  /** One line, shown in logs and docs. */
  description: string
  /** Runs inside a transaction. Use only the adapter it receives. */
  up: (db: IDBAdapter) => Promise<void>
}

export interface SchemaDefinition {
  baseline: string
  /** The version a brand-new database is at once the baseline is applied. */
  baselineVersion: number
  migrations: readonly Migration[]
}

export interface MigrationReport {
  from: number
  to: number
  applied: number[]
}

export async function getUserVersion(db: IDBAdapter): Promise<number> {
  const row = await db.queryOne<{ user_version: number }>('PRAGMA user_version')
  return row?.user_version ?? 0
}

/** Does a column exist? (SQLite has no `ADD COLUMN IF NOT EXISTS`.) */
export async function hasColumn(db: IDBAdapter, table: string, column: string): Promise<boolean> {
  const cols = await db.query<{ name: string }>(`PRAGMA table_info(${table})`)
  return cols.some((c) => c.name === column)
}

/** `ALTER TABLE … ADD COLUMN` that is a no-op when the column already exists. */
export async function addColumnIfMissing(db: IDBAdapter, table: string, column: string, definition: string): Promise<void> {
  if (!(await hasColumn(db, table, column))) {
    await db.run(`ALTER TABLE ${table} ADD COLUMN ${column} ${definition}`)
  }
}

/**
 * Bring a database up to date: apply the baseline (idempotent), then every
 * migration newer than its `user_version`, each in its own transaction so a
 * failure leaves the database at the last good version.
 */
export async function applySchema(db: IDBAdapter, schema: SchemaDefinition): Promise<MigrationReport> {
  assertOrdered(schema)

  const before = await getUserVersion(db)
  await db.run(schema.baseline)

  // 0 means "never versioned": a brand-new database, which the baseline just
  // created at baseline layout.
  let current = before === 0 ? schema.baselineVersion : before
  if (before === 0) await db.run(`PRAGMA user_version = ${current}`)

  const applied: number[] = []
  for (const migration of schema.migrations) {
    if (migration.version <= current) continue
    await db.transaction(async (tx) => {
      await migration.up(tx)
      await tx.run(`PRAGMA user_version = ${migration.version}`)
    })
    current = migration.version
    applied.push(migration.version)
  }

  if (applied.length > 0) await db.flushNow?.()
  return { from: before, to: current, applied }
}

function assertOrdered(schema: SchemaDefinition): void {
  let last = schema.baselineVersion
  for (const m of schema.migrations) {
    if (m.version <= last) {
      throw new Error(`Migration ${m.version} (${m.description}) is not after version ${last}`)
    }
    last = m.version
  }
}
